import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { contentReportsAPI, ContentReportRequest, track } from '../../services/api';

type EntityType = ContentReportRequest['entity_type'];
type Category = ContentReportRequest['category'];

interface ReportableProps {
  entityType: EntityType;
  entityId: string;
  /** Kept for API compatibility with existing call sites. The one-tap report
   *  flow no longer asks "where" — it submits the category alone, matching the
   *  Telegram bot — so this is currently unused. */
  subTargets?: string[];
  className?: string;
  children: React.ReactNode;
}

// The three predefined reasons, kept IN SYNC with the Telegram bot chips
// (telegram-bot/handlers/admin_report.py CATEGORY_CHIPS) so web + bot funnel
// into the same categories. Tap a chip → submitted, no free-text, no confirm.
const CHIPS: { value: Category; label: string }[] = [
  { value: 'wrong_answer', label: '🔴 Wrong answer' },
  { value: 'unclear', label: '😕 Confusing' },
  { value: 'broken', label: '🧱 Broken / typo' },
];

type Status = 'idle' | 'submitting' | 'done' | 'error';

export const Reportable: React.FC<ReportableProps> = ({
  entityType,
  entityId,
  className,
  children,
}) => {
  const { canFlagContent } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!canFlagContent) return <>{children}</>;

  const submit = async (category: Category) => {
    if (status === 'submitting') return;
    setStatus('submitting');
    setError(null);
    track('content_report.web.chip_tapped', { entity_type: entityType, category });
    try {
      await contentReportsAPI.submit({
        surface: 'web',
        entity_type: entityType,
        entity_id: entityId,
        category,
        note: '',
        location_ref: window.location.pathname + window.location.search,
      });
      setStatus('done');
      // Auto-dismiss the "thanks" so the surface returns to normal.
      window.setTimeout(() => {
        setOpen(false);
        setStatus('idle');
      }, 1300);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not report. Try again.');
      setStatus('error');
    }
  };

  return (
    <div className={`reportable-wrap ${className || ''}`} style={{ position: 'relative' }}>
      {children}
      <button
        type="button"
        aria-label="Report a problem"
        title="Report a problem"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          setStatus('idle');
          setError(null);
        }}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 24,
          height: 24,
          borderRadius: 12,
          border: '1px solid rgba(220, 38, 38, 0.4)',
          background: 'rgba(254, 226, 226, 0.85)',
          color: '#b91c1c',
          fontSize: 12,
          lineHeight: '22px',
          padding: 0,
          cursor: 'pointer',
          zIndex: 5,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        🚩
      </button>

      {open && (
        <ReportPopover
          status={status}
          error={error}
          onPick={submit}
          onClose={() => {
            setOpen(false);
            setStatus('idle');
            setError(null);
          }}
        />
      )}
    </div>
  );
};

interface ReportPopoverProps {
  status: Status;
  error: string | null;
  onPick: (category: Category) => void;
  onClose: () => void;
}

// Small anchored card under the 🚩 — light "tap and done" picker, not a modal.
const ReportPopover: React.FC<ReportPopoverProps> = ({ status, error, onPick, onClose }) => (
  <>
    {/* Click-away catcher. */}
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 40 }}
    />
    <div
      role="dialog"
      aria-label="Report a problem"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: 32,
        right: 4,
        zIndex: 50,
        width: 200,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
        padding: 10,
      }}
    >
      {status === 'done' ? (
        <p style={{ margin: 0, padding: '6px 4px', color: '#059669', fontSize: 13, fontWeight: 600 }}>
          ✓ Thanks — we'll take a look.
        </p>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '2px 4px 8px' }}>
            What's wrong?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {CHIPS.map((c) => (
              <button
                key={c.value}
                type="button"
                disabled={status === 'submitting'}
                onClick={() => onPick(c.value)}
                style={{
                  textAlign: 'left',
                  padding: '9px 10px',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  color: '#111827',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: status === 'submitting' ? 'wait' : 'pointer',
                  opacity: status === 'submitting' ? 0.6 : 1,
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
          {error && (
            <p style={{ color: '#b91c1c', fontSize: 11, margin: '8px 4px 0' }}>{error}</p>
          )}
        </>
      )}
    </div>
  </>
);

export default Reportable;
